import * as amplitude from '@amplitude/analytics-browser';
import { Identify } from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

import { API_KEY_ENV, PROPERTY_ENV } from '../const';
import { dateToIsoDateString } from '../dateUtils';
import { omitUndefined } from './analytics';
import { BillingCycleType, ConversionStageType, UserProperties } from './events';

// ###################################################################################
// # LOCAL UTILITY FUNCTIONS                                                         #
// ###################################################################################

const amp = amplitude.createInstance();

/**
 * Initialize the Amplitude instance.
 *
 * - Uses the environment API key (`API_KEY_ENV`).
 * - Sets an `instanceName` to distinguish environments (e.g. staging vs production).
 * - Restricts log output to warnings.
 * - Installs Session Replay plugin with proper configuration:
 *    - sampleRate: 1 (100% sampling) → adjust based on your session quota
 *    - forceSessionTracking: true → ensures session events are captured for replay linking
 *    - debugMode: false → set to true only during development debugging
 * - Explicitly enables session tracking via defaultTracking configuration.
 * - Enables/disables autocapture options:
 *    - attribution: true → track marketing attribution
 *    - fileDownloads: true → track file download events
 *    - formInteractions: true → track form input/submit events
 *    - pageViews: true → track page navigation
 *    - sessions: true → track session lifecycle
 *    - elementInteractions: false → recommended to keep off (too noisy / may capture sensitive data)
 *    - frustrationInteractions: false → experimental, keep off
 *    - networkTracking: false → keep off to avoid leaking PII
 *    - webVitals: false → experimental, keep off
 */
export function initAmplitude() {
  // Create and install Session Replay plugin with proper configuration
  const sessionReplayTracking = sessionReplayPlugin({
    sampleRate: 1, // 100% sampling rate as requested
    forceSessionTracking: true, // Ensure session tracking is enabled for replay linking
    debugMode: false, // Set to true only for debugging
  });
  amp.add(sessionReplayTracking);

  amp.init(API_KEY_ENV, undefined, {
    instanceName: PROPERTY_ENV,
    logLevel: amplitude.Types.LogLevel.Warn,
    defaultTracking: {
      sessions: true, // Explicitly ensure session tracking is enabled
    },
    autocapture: {
      attribution: true,
      fileDownloads: true,
      formInteractions: true,
      pageViews: true,
      sessions: true,
      elementInteractions: false,
      frustrationInteractions: false,
      networkTracking: false,
      webVitals: false,
    },
  });
}

// ###################################################################################
// # TRACKING EVENTS FUNCTIONS                                                       #
// ###################################################################################

/**
 * Standardized core event properties.
 *
 * - category: logical grouping of the event (e.g. "campaigns_list/campaign")
 * - label: descriptive label for the action (e.g. "delete_button")
 * - value: numeric measurement associated with the event (e.g. credits spent)
 * - properties: free-form key/value pairs for event-specific details
 */
interface CoreProps {
  category?: string;
  label?: string;
  value?: number;
  properties?: Record<string, string | number | boolean | null>;
}

/**
 * Track a custom event in Amplitude with consistent core props.
 *
 * @param eventName - The event type identifier (e.g. "click_campaign_delete")
 * @param props - Optional event properties:
 *   - category: logical grouping (e.g. "editor")
 *   - label: descriptive label (e.g. "SEO Tab")
 *   - value: numeric measurement (e.g. credits spent)
 *   - properties: any additional event-specific properties
 *
 * Example:
 * amplitudeTrackEvent('click_campaign_delete', {
 *   category: 'campaigns_list/campaign',
 *   properties: { item_name: 'Summer Sale' },
 * });
 */
export function amplitudeTrackEvent(eventName: string, props: CoreProps = {}) {
  const { category, label, value, properties = {} } = props;

  amp.track({
    event_type: eventName,
    event_properties: {
      env: PROPERTY_ENV,
      ...omitUndefined({ category, label, value }),
      ...properties,
    },
  });
}

// ###################################################################################
// # USER PROPERTIES FUNCTIONS                                                       #
// ###################################################################################

/**
 * Checks if the user ID property exists in Amplitude.
 * @returns {boolean} - True if the user ID is set, false otherwise.
 */
export function isAmplitudeUserPropertyUserIdExist(): boolean {
  return !!amp.getUserId();
}

/**
 * Sets the user ID property in Amplitude.
 * @param {string} userId - The user's unique identifier.
 */
export function setAmplitudeUserPropertyUserId(userId: string) {
  amp.setUserId(userId);
  amp.identify(new Identify().set(UserProperties.USER_ID, userId));
}

/**
 * Sets the 'signup_date' user property to the current date.
 */
export function setAmplitudeUserPropertySignedUpDate() {
  amp.identify(
    new Identify().set(UserProperties.SIGNUP_DATE, dateToIsoDateString(new Date()))
  );
}

/**
 * Sets the 'conversion_stage' user property.
 * @param {string} conversionStage - The user's current plan or conversion stage.
 */
export function setAmplitudeUserPropertyConversionStage(
  conversionStage: ConversionStageType
) {
  amp.identify(new Identify().set(UserProperties.CONVERSION_STAGE, conversionStage));
}

/**
 * Increments the 'campaigns_generated' user property by one.
 */
export function incrementAmplitudeUserPropertyCampaignsGenerated() {
  amp.identify(new Identify().add(UserProperties.CAMPAIGNS_GENERATED, 1));
}

/**
 * Sets the absolute value of campaigns_generated (instead of incrementing).
 * Used when syncing from Firestore backend data.
 * @param {number} count - The absolute count to set
 */
export function setAmplitudeUserPropertyCampaignsGenerated(count: number) {
  amp.identify(new Identify().set(UserProperties.CAMPAIGNS_GENERATED, count));
}

/**
 * Sets the 'signup_source' user property.
 * @param {string} source - The source of the user's signup (e.g., ad campaign, referral).
 */
export function setAmplitudeUserPropertySignupSource(source: string) {
  amp.identify(new Identify().set(UserProperties.SIGNUP_SOURCE, source));
}

/**
 * Sets the 'last_active_date' user property to the current date.
 */
export function setAmplitudeUserPropertyLastActiveDate() {
  amp.identify(
    new Identify().set(UserProperties.LAST_ACTIVE_DATE, dateToIsoDateString(new Date()))
  );
}

/**
 * Sets the 'billing_cycle' user property.
 * @param {string} cycle - The user's billing cycle (e.g., monthly, yearly).
 */
export function setAmplitudeUserPropertyBillingCycle(cycle: BillingCycleType) {
  amp.identify(new Identify().set(UserProperties.BILLING_CYCLE, cycle));
}

// ###################################################################################
// # SESSION REPLAY UTILITIES                                                        #
// ###################################################################################

/**
 * Debug utility to check Session Replay status and configuration.
 * Call this function in development to verify Session Replay is working correctly.
 *
 * @returns Object with Session Replay status information
 */
export function debugSessionReplay() {
  const deviceId = amp.getDeviceId();
  const sessionId = amp.getSessionId();
  const userId = amp.getUserId();

  const debugInfo = {
    deviceId,
    sessionId,
    userId,
    timestamp: new Date().toISOString(),
    environment: PROPERTY_ENV,
  };

  console.log('[Session Replay Debug]', debugInfo);

  // Track a test event to verify Session Replay ID is being attached
  amp.track({
    event_type: '[Debug] Session Replay Test',
    event_properties: {
      debug_timestamp: debugInfo.timestamp,
      env: PROPERTY_ENV,
    },
  });

  return debugInfo;
}
